import React from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import {
  Autocomplete,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Add, Delete } from "@mui/icons-material";

import ConfirmDeleteModal from "components/Modal/ConfirmDeleteModal/ConfirmDeleteModal";
import { LEAD_TYPE_OPTIONS } from "static/constants";
import { isValidArray } from "utils/array";

import SubmitButton from "components/Button/SubmitButton";
import useLeadInformationForm from "components/pages/transmissions/modal/patient/tabs/deviceInformation/leadInformation/form/useLeadInformationForm";
import { getDateJsFromDateString, getFormattedDate } from "utils/date";
import { DATE_FORMAT } from "static/date";

// Define the validation schema using Yup
const validationSchema = Yup.object().shape({
  leads: Yup.array().of(
    Yup.object().shape({
      type: Yup.string().required("Lead Type is required"),
      manufacturer: Yup.string().required("Manufacturer is required"),
      doi: Yup.string()
        .required("Date of Implant is required")
        .test(
          "is-valid-date",
          "Date of Implant must be a valid date",
          (value) => {
            if (!value) return false; // Required field validation
            const parsedDate = new Date(value);
            return !isNaN(parsedDate.getTime()); // Check if it's a valid date
          }
        ),
      model: Yup.string().required("Model Number is required"),
      serial_number: Yup.string().required("Serial Number is required"),
    })
  ),
});

const LeadInformationForm = ({
  leadFormData,
  setLeadFormData,
  setInitialLeads,
  handleCancel,
  isLoading,
  handleSubmit,
}) => {
  const {
    showDeleteModal,
    setShowDeleteModal,
    getLeadDeleteBodyText,
    leadIndexToDelete,
    confirmDelete,
    filteredManufacturers,
    getOptionValue,
    autoCompleteFilter,
  } = useLeadInformationForm({
    leadFormData,
    setLeadFormData,
    setInitialLeads,
  });

  return (
    <Formik
      initialValues={{ leads: leadFormData }}
      validationSchema={validationSchema}
      onSubmit={(values) => handleSubmit(values.leads)}
    >
      {({
        values,
        handleChange,
        setFieldValue,
        errors,
        touched,
        handleSubmit,
        resetForm,
      }) => (
        <Form>
          <FieldArray
            name="leads"
            render={({ push, remove }) => (
              <>
                <Grid container spacing={1} alignItems="center">
                  {values.leads.map((data, index) => (
                    <Grid container columnSpacing={2} item key={index}>
                      <Grid item style={{ width: "15%" }}>
                        <FormControl fullWidth variant="outlined" margin="none">
                          <InputLabel
                            id="demo-simple-select-label"
                            style={{ background: "white" }}
                          >
                            Lead Type
                          </InputLabel>
                          <Select
                            name={`leads[${index}].type`}
                            variant="outlined"
                            value={data.type || ""}
                            onChange={handleChange}
                            error={
                              touched?.leads?.[index]?.type &&
                              !!errors?.leads?.[index]?.type
                            }
                          >
                            {LEAD_TYPE_OPTIONS.map((lead, idx) => (
                              <MenuItem value={lead} key={idx}>
                                {lead}
                              </MenuItem>
                            ))}
                          </Select>

                          {touched?.leads?.[index]?.type &&
                            !!errors?.leads?.[index]?.type && (
                              <FormHelperText error>
                                {errors?.leads?.[index]?.type}
                              </FormHelperText>
                            )}
                        </FormControl>
                      </Grid>

                      <Grid item style={{ width: "15%" }}>
                        <FormControl fullWidth variant="outlined" margin="none">
                          <Autocomplete
                            freeSolo
                            id={`leads[${index}].manufacturer`}
                            name={`leads[${index}].manufacturer`}
                            openOnFocus={true}
                            clearOnBlur={true}
                            value={data.manufacturer}
                            getOptionLabel={getOptionValue}
                            options={
                              filteredManufacturers &&
                              isValidArray(filteredManufacturers)
                                ? filteredManufacturers
                                : []
                            }
                            onChange={(e, newValue) => {
                              const selectedManufacturer =
                                getOptionValue(newValue);

                              setFieldValue(
                                `leads[${index}].manufacturer`,
                                selectedManufacturer
                              );
                            }}
                            filterOptions={(options, params) => {
                              const filtered = autoCompleteFilter(
                                options,
                                params
                              );
                              if (params.inputValue !== "") {
                                filtered.unshift({
                                  inputValue: params.inputValue,
                                  title: `+ Add "${params.inputValue}"`,
                                });
                              }
                              return filtered;
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Manufacturer"
                                variant="outlined"
                                placeholder="Manufacturer"
                                error={
                                  touched?.leads?.[index]?.manufacturer &&
                                  !!errors?.leads?.[index]?.manufacturer
                                }
                                helperText={
                                  touched?.leads?.[index]?.manufacturer &&
                                  errors?.leads?.[index]?.manufacturer
                                }
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid
                        item
                        style={{ width: "15%" }}
                        alignSelf="flex-start"
                      >
                        <FormControl fullWidth margin="none">
                          <DatePicker
                            margin="none"
                            inputVariant="outlined"
                            id={`leads[${index}].doi`}
                            label="Date of Implant"
                            name={`leads[${index}].doi`}
                            format={DATE_FORMAT.DEFAULT}
                            value={
                              data.doi
                                ? getDateJsFromDateString(data.doi)
                                : null
                            }
                            onChange={(date) =>
                              setFieldValue(
                                `leads[${index}].doi`,
                                getFormattedDate(date)
                              )
                            }
                            slotProps={{
                              textField: {
                                required: true,
                                fullWidth: true,
                                error:
                                  touched?.leads?.[index]?.doi &&
                                  !!errors?.leads?.[index]?.doi,
                                helperText:
                                  touched?.leads?.[index]?.doi &&
                                  errors?.leads?.[index]?.doi,
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>

                      <Grid
                        item
                        style={{ width: "15%" }}
                        alignSelf="flex-start"
                      >
                        <TextField
                          required
                          fullWidth
                          margin="none"
                          variant="outlined"
                          name={`leads[${index}].model`}
                          label="Model Number"
                          value={data.model || ""}
                          onChange={handleChange}
                          error={
                            touched?.leads?.[index]?.model &&
                            !!errors?.leads?.[index]?.model
                          }
                          helperText={
                            touched?.leads?.[index]?.model &&
                            errors?.leads?.[index]?.model
                          }
                        />
                      </Grid>

                      <Grid item style={{ width: "15%" }}>
                        <TextField
                          required
                          fullWidth
                          margin="none"
                          variant="outlined"
                          name={`leads[${index}].serial_number`}
                          label="Serial Number"
                          value={data.serial_number || ""}
                          onChange={handleChange}
                          error={
                            touched?.leads?.[index]?.serial_number &&
                            !!errors?.leads?.[index]?.serial_number
                          }
                          helperText={
                            touched?.leads?.[index]?.serial_number &&
                            errors?.leads?.[index]?.serial_number
                          }
                        />
                      </Grid>

                      <Grid
                        item
                        alignItems="center"
                        sx={{
                          width: {
                            marginTop: "8px",
                          },
                        }}
                      >
                        <Grid item display="flex" gap={2} alignItems="center">
                          <FormControlLabel
                            label="Active"
                            labelPlacement="start"
                            control={
                              <Field
                                as={Switch}
                                name={`leads[${index}].active`}
                                checked={data.active || false}
                                onChange={() => {
                                  setFieldValue(
                                    `leads[${index}].active`,
                                    !data.active
                                  );
                                }}
                              />
                            }
                            style={{
                              gap: "6px",
                              marginLeft: 0,
                            }}
                          />
                          <FormControlLabel
                            label="Advisory"
                            labelPlacement="start"
                            control={
                              <Field
                                as={Switch}
                                name={`leads[${index}].advisory`}
                                checked={data.advisory || false}
                                onChange={() => {
                                  setFieldValue(
                                    `leads[${index}].advisory`,
                                    !data.advisory
                                  );
                                }}
                              />
                            }
                            style={{
                              gap: "6px",
                              marginLeft: 0,
                            }}
                          />

                          {values.leads.length > 1 && (
                            <Delete
                              className="rpm-device-information__delete"
                              onClick={() => remove(index)}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                  <Grid item>
                    <span
                      className="rpm-device-information__add-rpm-device"
                      onClick={() =>
                        push({
                          type: "",
                          manufacturer: "",
                          doi: null,
                          model: "",
                          serial_number: "",
                          active: false,
                          advisory: false,
                        })
                      }
                    >
                      <Add className="rpm-device-information__add-icon" />
                      <span className="rpm-device-information__add-text">
                        Add Lead
                      </span>
                    </span>
                  </Grid>
                </Grid>
              </>
            )}
          />

          <div
            className="d-flex flex-end"
            style={{ textAlign: "right", gap: "10px" }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                handleCancel();
                resetForm();
              }}
            >
              Cancel
            </Button>
            <SubmitButton
              variant="contained"
              color="secondary"
              handleClick={handleSubmit}
              isLoading={isLoading}
            />
          </div>

          {showDeleteModal && (
            <ConfirmDeleteModal
              isLoading={false}
              isModalOpen={showDeleteModal}
              modalBodyText={getLeadDeleteBodyText()}
              onClose={() => setShowDeleteModal(false)}
              onDelete={() => confirmDelete(leadIndexToDelete)}
            />
          )}
        </Form>
      )}
    </Formik>
  );
};

export default LeadInformationForm;
